import { DatePipe } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, ViewChild, computed, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { MethodsUtils } from '../../../shared/util/methods.utils';
import { InfoArquivo } from '../../models/info-arquivo.model';
import { animacaoFade, animacaoPop } from '../../utils/chat-animacoes.utils';
import { PlayerVideoComponent } from './player-video/player-video.component';
import { GaleriaMidiaData } from './models/galeria-midia-data.model';
import { GaleriaMidiaResultado } from './models/galeria-midia-resultado.model';
import { ItemGaleria } from './models/item-galeria.model';
import { Ponto } from './models/ponto.model';
import { TipoItemGaleria } from './models/tipo-item-galeria.model';
import {
  DESLOCAMENTO_ENTRADA_SLIDE, DURACAO_ENTRADA_SLIDE_MS, DURACAO_SAIDA_SLIDE_MS, ESCALA_DUPLO_TOQUE, ESCALA_MAX, ESCALA_MIN,
  LIMIAR_GESTO_HORIZONTAL_PX, LIMIAR_TROCA_SLIDE_PX, PASSO_BOTAO, RESISTENCIA_BORDA_SLIDE, VELOCIDADE_TROCA_SLIDE
} from './utils/galeria-midia.constantes';

@Component({
  selector: 'app-galeria-midia',
  standalone: true,
  imports: [DatePipe, MatIcon, MatTooltip, PlayerVideoComponent],
  animations: [animacaoPop, animacaoFade],
  templateUrl: './galeria-midia.component.html',
  styleUrl: './galeria-midia.component.css'
})
export class GaleriaMidiaComponent implements AfterViewInit {
  private readonly data = inject<GaleriaMidiaData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<GaleriaMidiaComponent>);

  @ViewChild('miniaturas') miniaturas?: ElementRef<HTMLElement>;
  @ViewChild('palco') palco?: ElementRef<HTMLElement>;
  @ViewChild('imagem') imagem?: ElementRef<HTMLImageElement>;

  readonly itens = signal<ItemGaleria[]>(this.data.itens ?? []);
  indice = signal(Math.min(Math.max(this.data.indiceInicial ?? 0, 0), Math.max((this.data.itens?.length ?? 1) - 1, 0)));
  readonly atual = computed<ItemGaleria | undefined>(() => this.itens()[this.indice()]);
  readonly tipoAtual = computed<TipoItemGaleria>(() => this.atual()?.tipo ?? 'imagem');
  readonly ehImagem = computed(() => this.tipoAtual() === 'imagem');

  private readonly sanitizer = inject(DomSanitizer);
  private readonly urlsSeguras = new Map<string, SafeResourceUrl>();
  temMaisAnteriores = signal(!!this.data.temMaisAnteriores && !!this.data.carregarAnteriores);
  carregandoAnteriores = signal(false);

  readonly comErro = signal<ReadonlySet<string>>(new Set());
  readonly atualComErro = computed(() => this.comErro().has(this.atual()?.url ?? ''));

  menuContexto = signal<{ x: number; y: number } | null>(null);
  carregandoImagem = signal(true);

  slideX = signal('0px');
  opacidadeSlide = signal(1);
  transicaoSlide = signal(false);
  private animandoSlide = false;
  private arrastandoSlide = false;
  private inicioArrasteSlide = 0;

  ngAfterViewInit(): void {
    this.conferirImagemPronta();
  }

  urlSegura(url: string): SafeResourceUrl {
    let segura = this.urlsSeguras.get(url);
    if (!segura) {
      segura = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      this.urlsSeguras.set(url, segura);
    }
    return segura;
  }

  nomeArquivo(item: ItemGaleria): string {
    return MethodsUtils.nomeDoArquivo(item.url);
  }

  infoArquivo(item: ItemGaleria): InfoArquivo {
    return MethodsUtils.infoArquivo(this.extensao(item));
  }

  extensao(item: ItemGaleria): string {
    return MethodsUtils.extensaoDoArquivo(item.url);
  }

  private conferirImagemPronta(): void {
    setTimeout(() => {
      if (!this.ehImagem()) {
        this.carregandoImagem.set(false);
        return;
      }
      const img = this.imagem?.nativeElement;
      if (img?.complete && img.naturalWidth > 0) this.carregandoImagem.set(false);
    }, 0);
  }

  escala = signal(ESCALA_MIN);
  deslocamento = signal<Ponto>({ x: 0, y: 0 });
  interagindo = signal(false);
  readonly ampliada = computed(() => this.escala() > ESCALA_MIN + 0.01);
  readonly transformacao = computed(() => {
    const { x, y } = this.deslocamento();
    return `translate3d(${x}px, ${y}px, 0) scale(${this.escala()})`;
  });

  private readonly ponteiros = new Map<number, Ponto>();
  private inicioGesto: { pontos: Ponto[]; escala: number; deslocamento: Ponto; distancia: number; centro: Ponto } | null = null;
  private moveuNoGesto = false;
  private ultimoToque = 0;
  private pressionouNoFundo = false;
  private tipoPonteiro = 'mouse';

  @HostListener('document:keydown', ['$event'])
  aoTeclar(event: KeyboardEvent): void {
    if (this.menuContexto() && event.key === 'Escape') {
      this.menuContexto.set(null);
      return;
    }
    if (event.key === 'ArrowRight') this.proxima();
    else if (event.key === 'ArrowLeft') this.anterior();
    else if (event.key === 'Escape') this.validarIsAmpliadaSeguindo();
    else if (event.key === '+' || event.key === '=') this.ampliar();
    else if (event.key === '-') this.reduzir();
    else if (event.key === '0') this.restaurarZoom();
  }

  validarIsAmpliadaSeguindo(){
    if(this.ampliada()){
      this.restaurarZoom()
    }else{
      this.fechar();
    }
  }

  anterior(): void {
    if (this.indice() > 0) this.navegarPara(this.indice() - 1);
    else if (this.temMaisAnteriores()) {
      this.voltarSlide();
      this.carregarAnteriores(true);
    } else {
      this.voltarSlide();
    }
  }

  proxima(): void {
    if (this.indice() < this.itens().length - 1) this.navegarPara(this.indice() + 1);
    else this.voltarSlide();
  }

  navegarPara(indice: number): void {
    if (indice === this.indice()) {
      this.voltarSlide();
      return;
    }
    const direcao = indice > this.indice() ? 1 : -1;
    if (this.animandoSlide) {
      this.irPara(indice);
      return;
    }
    this.animandoSlide = true;
    this.transicaoSlide.set(true);
    this.slideX.set(`${-direcao * 100}%`);
    this.opacidadeSlide.set(0);
    setTimeout(() => {
      this.transicaoSlide.set(false);
      this.irPara(indice);
      this.slideX.set(`${direcao * DESLOCAMENTO_ENTRADA_SLIDE}%`);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        this.transicaoSlide.set(true);
        this.slideX.set('0px');
        this.opacidadeSlide.set(1);
        setTimeout(() => (this.animandoSlide = false), DURACAO_ENTRADA_SLIDE_MS);
      }));
    }, DURACAO_SAIDA_SLIDE_MS);
  }

  private voltarSlide(): void {
    if (this.animandoSlide) return;
    this.transicaoSlide.set(true);
    this.slideX.set('0px');
    this.opacidadeSlide.set(1);
  }

  private temVizinho(dx: number): boolean {
    return dx < 0 ? this.indice() < this.itens().length - 1 : this.indice() > 0 || this.temMaisAnteriores();
  }

  async carregarAnteriores(irParaAnterior = false): Promise<void> {
    const carregar = this.data.carregarAnteriores;
    if (!carregar || this.carregandoAnteriores()) return;
    this.carregandoAnteriores.set(true);
    const urlAtual = this.atual()?.url;
    try {
      const { itens, temMais } = await carregar();
      this.itens.set(itens);
      this.temMaisAnteriores.set(temMais);
      const novoIndice = Math.max(0, itens.findIndex(i => i.url === urlAtual));
      this.indice.set(novoIndice);
      this.conferirImagemPronta();
      if (irParaAnterior && novoIndice > 0) this.navegarPara(novoIndice - 1);
    } catch {
      this.temMaisAnteriores.set(false);
    } finally {
      this.carregandoAnteriores.set(false);
    }
  }

  abrirMenuContexto(event: MouseEvent): void {
    if (!this.atual()) return;
    event.preventDefault();
    event.stopPropagation();
    const x = Math.min(event.clientX, window.innerWidth - 200);
    const y = Math.min(event.clientY, window.innerHeight - 110);
    this.menuContexto.set({ x, y });
  }

  fecharMenuContexto(): void {
    this.menuContexto.set(null);
  }

  abrirNoChat(): void {
    const item = this.atual();
    this.menuContexto.set(null);
    if (!item) return;
    this.dialogRef.close({ abrirNoChat: { id: item.mensagemId, metaId: item.metaId } } as GaleriaMidiaResultado);
  }

  irPara(indice: number): void {
    if (indice === this.indice()) return;
    this.indice.set(indice);
    const item = this.itens()[indice];
    this.carregandoImagem.set((item?.tipo ?? 'imagem') === 'imagem' && !this.comErro().has(item?.url ?? ''));
    this.restaurarZoom(false);
    this.conferirImagemPronta();
    setTimeout(() => {
      this.miniaturas?.nativeElement.querySelector('.galeria-miniatura-ativa')
        ?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }, 0);
  }

  aoCarregarImagem(): void {
    this.carregandoImagem.set(false);
  }

  aoFalharImagem(url: string): void {
    const novos = new Set(this.comErro());
    novos.add(url);
    this.comErro.set(novos);
    if (url === this.atual()?.url) this.carregandoImagem.set(false);
  }

  ampliar(): void {
    if (!this.ehImagem()) return;
    this.aplicarEscala(this.escala() + PASSO_BOTAO, this.centroDoPalco());
  }

  reduzir(): void {
    this.aplicarEscala(this.escala() - PASSO_BOTAO, this.centroDoPalco());
  }

  restaurarZoom(animar = true): void {
    this.interagindo.set(!animar);
    this.escala.set(ESCALA_MIN);
    this.deslocamento.set({ x: 0, y: 0 });
  }

  aoRolar(event: WheelEvent): void {
    if (this.atualComErro() || !this.ehImagem()) return;
    event.preventDefault();
    const fator = Math.exp(-event.deltaY * (event.ctrlKey ? 0.01 : 0.0025));
    this.interagindo.set(true);
    this.aplicarEscala(this.escala() * fator, { x: event.clientX, y: event.clientY });
  }

  aoDuploClique(event: MouseEvent): void {
    event.stopPropagation();
    if (this.tipoPonteiro !== 'mouse') return;
    this.alternarZoomNoPonto({ x: event.clientX, y: event.clientY });
  }

  private alternarZoomNoPonto(ponto: Ponto): void {
    if (this.atualComErro() || !this.ehImagem()) return;
    this.interagindo.set(false);
    if (this.ampliada()) this.restaurarZoom();
    else this.aplicarEscala(ESCALA_DUPLO_TOQUE, ponto);
  }

  private aplicarEscala(novaEscala: number, pontoTela: Ponto, base?: { escala: number; deslocamento: Ponto }): void {
    const escalaAnterior = base?.escala ?? this.escala();
    const deslocAnterior = base?.deslocamento ?? this.deslocamento();
    const escala = Math.min(ESCALA_MAX, Math.max(ESCALA_MIN, novaEscala));
    const centro = this.centroDoPalco();
    const p = { x: pontoTela.x - centro.x, y: pontoTela.y - centro.y };
    const razao = escala / escalaAnterior;
    this.escala.set(escala);
    this.deslocamento.set(this.limitarDeslocamento({
      x: p.x - (p.x - deslocAnterior.x) * razao,
      y: p.y - (p.y - deslocAnterior.y) * razao
    }, escala));
  }

  private limitarDeslocamento(desloc: Ponto, escala = this.escala()): Ponto {
    const img = this.imagem?.nativeElement;
    const palco = this.palco?.nativeElement;
    if (!img || !palco || escala <= ESCALA_MIN) return { x: 0, y: 0 };
    const maxX = Math.max(0, (img.offsetWidth * escala - palco.clientWidth) / 2);
    const maxY = Math.max(0, (img.offsetHeight * escala - palco.clientHeight) / 2);
    return {
      x: Math.min(maxX, Math.max(-maxX, desloc.x)),
      y: Math.min(maxY, Math.max(-maxY, desloc.y))
    };
  }

  private centroDoPalco(): Ponto {
    const rect = this.palco?.nativeElement.getBoundingClientRect();
    return rect ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 } : { x: 0, y: 0 };
  }

  aoPressionar(event: PointerEvent): void {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.menuContexto.set(null);
    this.pressionouNoFundo = event.target === event.currentTarget
      || (event.target as HTMLElement)?.classList?.contains('galeria-slide');
    this.tipoPonteiro = event.pointerType;
    this.arrastandoSlide = false;
    this.inicioArrasteSlide = performance.now();
    if (!this.ehImagem() && !this.pressionouNoFundo) return;
    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
    this.ponteiros.set(event.pointerId, { x: event.clientX, y: event.clientY });
    this.moveuNoGesto = false;
    this.iniciarGesto();
  }

  aoMover(event: PointerEvent): void {
    if (!this.ponteiros.has(event.pointerId) || !this.inicioGesto) return;
    this.ponteiros.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const pontos = [...this.ponteiros.values()];
    const inicio = this.inicioGesto;

    if (pontos.length >= 2 && inicio.pontos.length >= 2 && this.ehImagem()) {
      this.moveuNoGesto = true;
      this.interagindo.set(true);
      const centro = this.pontoMedio(pontos);
      const escala = inicio.escala * (this.distancia(pontos) / (inicio.distancia || 1));
      const deslocBase = {
        x: inicio.deslocamento.x + (centro.x - inicio.centro.x),
        y: inicio.deslocamento.y + (centro.y - inicio.centro.y)
      };
      this.aplicarEscala(escala, centro, { escala: inicio.escala, deslocamento: deslocBase });
      return;
    }

    const dx = pontos[0].x - inicio.pontos[0].x;
    const dy = pontos[0].y - inicio.pontos[0].y;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) this.moveuNoGesto = true;
    if (this.ampliada()) {
      this.interagindo.set(true);
      this.deslocamento.set(this.limitarDeslocamento({ x: inicio.deslocamento.x + dx, y: inicio.deslocamento.y + dy }));
      return;
    }
    if (this.animandoSlide) return;
    if (!this.arrastandoSlide && Math.abs(dx) > LIMIAR_GESTO_HORIZONTAL_PX && Math.abs(dx) > Math.abs(dy)) this.arrastandoSlide = true;
    if (!this.arrastandoSlide) return;
    const fator = this.temVizinho(dx) ? 1 : RESISTENCIA_BORDA_SLIDE;
    this.transicaoSlide.set(false);
    this.slideX.set(`${dx * fator}px`);
  }

  aoSoltar(event: PointerEvent): void {
    const inicio = this.inicioGesto;
    const final = this.ponteiros.get(event.pointerId);
    this.ponteiros.delete(event.pointerId);
    this.interagindo.set(false);

    if (this.ponteiros.size > 0) {
      this.iniciarGesto();
      return;
    }
    this.inicioGesto = null;
    if (!inicio || !final || inicio.pontos.length !== 1) return;

    const dx = final.x - inicio.pontos[0].x;
    const dy = final.y - inicio.pontos[0].y;
    if (this.arrastandoSlide) {
      this.arrastandoSlide = false;
      const velocidade = Math.abs(dx) / Math.max(1, performance.now() - this.inicioArrasteSlide);
      const trocar = this.temVizinho(dx) && (Math.abs(dx) > LIMIAR_TROCA_SLIDE_PX || velocidade > VELOCIDADE_TROCA_SLIDE);
      if (!trocar) this.voltarSlide();
      else if (dx < 0) this.proxima();
      else this.anterior();
      return;
    }
    if (event.pointerType !== 'mouse' && !this.moveuNoGesto) {
      const agora = Date.now();
      if (agora - this.ultimoToque < 300) {
        this.ultimoToque = 0;
        this.alternarZoomNoPonto(final);
        return;
      }
      this.ultimoToque = agora;
    }
  }

  aoCancelar(event: PointerEvent): void {
    this.ponteiros.delete(event.pointerId);
    if (this.ponteiros.size === 0) {
      this.inicioGesto = null;
      this.interagindo.set(false);
      if (this.arrastandoSlide) {
        this.arrastandoSlide = false;
        this.voltarSlide();
      }
    }
  }

  aoClicarPalco(): void {
    if (this.moveuNoGesto || this.ampliada() || !this.pressionouNoFundo || this.tipoPonteiro !== 'mouse') return;
    this.fechar();
  }

  private iniciarGesto(): void {
    const pontos = [...this.ponteiros.values()];
    this.inicioGesto = {
      pontos,
      escala: this.escala(),
      deslocamento: this.deslocamento(),
      distancia: this.distancia(pontos),
      centro: this.pontoMedio(pontos)
    };
  }

  private distancia(pontos: Ponto[]): number {
    if (pontos.length < 2) return 0;
    return Math.hypot(pontos[1].x - pontos[0].x, pontos[1].y - pontos[0].y);
  }

  private pontoMedio(pontos: Ponto[]): Ponto {
    if (pontos.length < 2) return pontos[0] ?? { x: 0, y: 0 };
    return { x: (pontos[0].x + pontos[1].x) / 2, y: (pontos[0].y + pontos[1].y) / 2 };
  }

  baixar(): void {
    const url = this.atual()?.url;
    if (!url || this.atualComErro()) return;
    MethodsUtils.baixarArquivo(url, MethodsUtils.nomeDoArquivo(url));
  }

  fechar(): void {
    this.dialogRef.close();
  }
}
